/* eslint-disable camelcase */
import { useHistory, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../hooks";
import AssignAssetToSubscriptionModal from "../Modal/AssignAssetToSubscriptionModal";

import { useNotifier } from "../../context";
import { enforceAccessRightCanRead, withAccessRights } from "../../wrappers";
import {
  ActionBar,
  ActionBarButtons,
  DeleteActionButton,
  EditActionButton,
} from "../ActionBar";
import { ConflictError } from "../../api";
import { formatDateTypes, url } from "../../utils";
import { Details, DetailsHeaderItem, DetailsItem } from "../Details";
import AssetStatus from "./AssetStatus";
import More from "../More";
import { Column, Container, Row } from "../Grid";
import Loader from "../Loader";
import AssetComponentsList from "./AssetComponentsList";
import NodesAssignList from "../ObjectAssignList/NodesAssignList";
import InstrumentationsAssignList from "../ObjectAssignList/InstrumentationsAssignList";
import AssetLastSeen from "./AssetLastSeen";
import {
  extractAssets,
  extractPictures,
  extractBestPictureUrl,
} from "../../extractors";
import AssetFirmwareVersion from "./AssetFirmwareVersion";
import SpecificationDetails from "../Specifications/SpecificationDetails";
import { accessRightsShape } from "../../shapes";
import ProductCodeDetails from "./ProductCodeDetails/ProductCodeDetails";
import AssetDetailsBasicInfo from "./AssetDetailsBasicInfo";
import NavigationBreadCrumbs from "../Breadcrumb/NavigationBreadCrumbs";
import { AssetDtoWithIncludes, AssetsDtoWithIncludes } from "../../types";

type OwnProps = {
  accessRights?: accessRightsShape;
  appSpecificDetailsItem?: React.ReactNode;
  moreContent?: React.ReactNode;
  shouldCollapse?: boolean;
  appSpecificContent?: React.ReactNode;
  onDeleteAsset?: $TSFixMeFunction;
};

const defaultProps: Partial<OwnProps> = {
  shouldCollapse: true,
  onDeleteAsset: () => {},
};

type Props = OwnProps & typeof defaultProps;

function AssetDetails({
  appSpecificDetailsItem,
  moreContent,
  appSpecificContent,
  shouldCollapse,
  onDeleteAsset,
  accessRights,
}: Props) {
  // @ts-expect-error TS(2339) FIXME: Property 'id' does not exist on type '{}'.
  const { id } = useParams();
  const intl = useIntl();
  const api = useApi();
  const history = useHistory();
  const notifier = useNotifier();
  const queryClient = useQueryClient();

  const t = (translationId: $TSFixMe) =>
    intl.formatMessage({ id: translationId });

  const includes = [
    "parent",
    "product",
    "product.pictures",
    "product.status",
    "product.manufacturer",
    "product.tenant",
    "status",
    "pictures",
    "instrumentations",
    "specifications",
    "additional_data_history_period",
  ] as const;

  const assetIncludes = [...includes, "global_id", "paths"] as const;

  const { data: assetResponse, isLoading } = api.get.useQuery<
    AssetDtoWithIncludes<typeof assetIncludes>
  >(`/assets/${id}`, {
    include: assetIncludes.join(","),
  });

  const { data: assets } = api.get.useQuery<
    AssetsDtoWithIncludes<typeof includes>
  >("/assets", {
    parent_id: id,
    include: includes.join(","),
  });

  const asset = assetResponse
    ? {
        ...assetResponse,
        itemType: "asset",
        productPictureUrl: extractBestPictureUrl(
          assetResponse?.product?.pictures,
        ),
        pictures: assetResponse.pictures
          ? extractPictures(assetResponse)
          : null,
        manufacturerName: assetResponse.product?.manufacturer?.name,
        productCode: assetResponse.product?.product_code,
        productTenantPublic: assetResponse.product?.tenant?.public,
        assetComponents: extractAssets(assets),
      }
    : undefined;

  const orderCodeLookUp =
    asset?.manufacturerName === "Endress+Hauser" &&
    asset.specifications?.["eh.pcps.tmp.ordercode"] &&
    /^[a-zA-Z0-9]+-+[a-zA-Z0-9]+\/[a-zA-Z0-9]+/g.exec(
      asset.specifications["eh.pcps.tmp.ordercode"].value,
    );
  const orderCode =
    asset?.specifications?.["eh.pcps.tmp.ordercode"]?.value ?? "";

  const { data: eocResp } = api.get.useQuery(
    "/endress/extended_order_code_lookup",
    { order_code: orderCode },
    {
      enabled: !!orderCodeLookUp,
      // @ts-expect-error TS(2345) FIXME: Argument of type '{ enabled: boolean; retries: boo... Remove this comment to see the full error message
      retries: false,
      meta: { useGlobalErrorHandling: false },
    },
  );

  if (asset) {
    // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
    asset.extendedOrderCode = eocResp?.extended_order_code;
  }

  const { mutate: deleteAsset } = api.delete.useMutation(`/assets/${id}`, {
    onSuccess: async () => {
      // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      onDeleteAsset();
      queryClient.invalidateQueries({ queryKey: ["/subscription"] });
      notifier.showSuccess(t`asset.actions.delete.notification`);
    },
    onError: (error: $TSFixMe) => {
      if (
        error instanceof ConflictError &&
        error.errors.length > 0 &&
        error.errors[0].type === "assigned_restriction"
      ) {
        notifier.showError(t`api.error.asset.assigned_restriction`);
      } else if (
        error instanceof ConflictError &&
        error.errors.length > 0 &&
        error.errors[0].type === "device_not_deactivated_conflict"
      ) {
        notifier.showError(t`api.error.device_not_deactivated_conflict`);
      } else {
        notifier.showError(t`api.error.unknown`);
      }
    },
  });

  const onConfirmDelete = () =>
    // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
    deleteAsset(null, {
      onSuccess: () => {
        history.push(url("/nodes"));
      },
    });

  const renderAssetDetails = () =>
    asset ? (
      <div>
        <AssetDetailsBasicInfo
          productPictureUrl={asset.productPictureUrl}
          pictures={asset.pictures}
          serialNumber={asset.serial_number}
          productName={asset.product?.name}
          manufacturerName={asset.product?.manufacturer?.name}
        />
        <AssetStatus asset={asset} />
        {asset.global_id ? (
          <DetailsHeaderItem
            id="asset-global-id"
            translationKey="label.global_asset_id"
            value={asset.global_id}
          />
        ) : null}
        {appSpecificDetailsItem}
        <SpecificationDetails specifications={asset.specifications} />
      </div>
    ) : null;

  // in_subscription will be undefined in id
  const fullAppFunctionality =
    asset && (asset.in_subscription === undefined || asset.in_subscription);
  const renderMoreDetails = () => {
    if (!asset || !fullAppFunctionality) return null;
    const productCode = asset.specifications?.["eh.pcps.tmp.ordercode"]?.value
      ? asset.specifications["eh.pcps.tmp.ordercode"].value
      : asset.product?.product_code;

    return (
      <div>
        <AssetLastSeen asset={asset} />
        <AssetFirmwareVersion asset={asset} />
        <DetailsItem
          id="asset-description"
          translationKey="label.description"
          value={asset.description}
        />
        <DetailsItem
          id="asset-product-code"
          translationKey="label.product_code"
        >
          <div className="product-code-wrapper">
            <span>{productCode}</span>
            {/* @ts-expect-error TS(2339) FIXME: Property 'extendedOrderCode' does not exist on type... Remove this comment to see the full error message */}
            {asset.extendedOrderCode && (
              <span className="additional-info">
                {/* @ts-expect-error TS(2339) FIXME: Property 'extendedOrderCode' does not exist on type... Remove this comment to see the full error message */}
                ({asset.extendedOrderCode})
              </span>
            )}
            {asset.product_variant && (
              <ProductCodeDetails
                productCode={productCode}
                // @ts-expect-error TS(2339) FIXME: Property 'extendedOrderCode' does not exist on type... Remove this comment to see the full error message
                productCodeExtended={asset.extendedOrderCode}
                productVariantId={asset.product_variant.id}
              />
            )}
          </div>
        </DetailsItem>
        <DetailsItem
          id="asset-production-date"
          translationKey="label.asset_production_date"
          value={formatDateTypes(asset.production_date, intl)}
        />
        {moreContent}
      </div>
    );
  };

  const renderAssignmentLists = () => (
    <Details>
      {/* @ts-expect-error TS(18048) FIXME: 'asset' is possibly 'undefined'. */}
      {asset.assetComponents?.length > 0 && (
        <AssetComponentsList
          // @ts-expect-error TS(18048) FIXME: 'asset' is possibly 'undefined'.
          assets={asset.assetComponents}
          accessRights={accessRights}
        />
      )}
      {/* @ts-expect-error TS(2322) FIXME: Type '{ object: any; accessRights: accessRightsSha... Remove this comment to see the full error message */}
      <InstrumentationsAssignList object={asset} accessRights={accessRights} />
      <NodesAssignList
        // @ts-expect-error TS(2322) FIXME: Type '{ object: any; accessRights: accessRightsSha... Remove this comment to see the full error message
        object={asset}
        accessRights={accessRights}
        testId="assigned_systems1"
      />
    </Details>
  );

  return (
    <Container>
      {/* @ts-expect-error TS(2339) FIXME: Property 'paths' does not exist on type... Remove this comment to see the full error message */}
      <NavigationBreadCrumbs paths={asset?.paths} />
      <Row>
        <Column>
          <ActionBar>
            <h1>{t`asset_details.header`}</h1>
            <ActionBarButtons>
              <EditActionButton
                id="edit-asset-button"
                disabled={!accessRights?.canUpdate}
                target={`/assets/${id}/edit`}
              />
              <DeleteActionButton
                id="delete-asset"
                disabled={!accessRights?.canDelete}
                modalTitle={t`asset.actions.delete.modal_title`}
                modalMessage={t`asset.actions.delete.modal_message`}
                onConfirm={onConfirmDelete}
              />
            </ActionBarButtons>
          </ActionBar>
          <Details>
            {renderAssetDetails()}
            {isLoading ? (
              <Loader id="asset-details-loader" loading={isLoading} />
            ) : (
              <div>
                {shouldCollapse ? (
                  <More>{renderMoreDetails()}</More>
                ) : (
                  renderMoreDetails()
                )}
              </div>
            )}
          </Details>
          {fullAppFunctionality && (
            <div>
              {appSpecificContent}
              {renderAssignmentLists()}
            </div>
          )}
          {asset && !fullAppFunctionality && (
            // @ts-expect-error TS(2322) FIXME: Type '{ asset: any; }' is not assignable to type '... Remove this comment to see the full error message
            <AssignAssetToSubscriptionModal asset={asset} />
          )}
        </Column>
      </Row>
    </Container>
  );
}

AssetDetails.defaultProps = defaultProps;

export default withAccessRights(
  AssetDetails,
  "Asset",
  enforceAccessRightCanRead,
);
